import { useEffect, useState, type FormEvent } from 'react';
import { validatePassword } from '@pos/core';
import { env } from '../../lib/env.ts';
import { mfaFactorForPasswordChange, verifyMfaCode } from '../../lib/mfa.ts';
import { authLinkOnLoad, supabase } from '../../lib/supabase.ts';
import { Alert, Button, Card, Field, Input, Spinner, errorMessage, PasswordInput } from '../../ui/index.tsx';

/**
 * Destino del link de invitación (alta de un dueño) o de recuperación de contraseña.
 * Supabase deja la sesión lista a partir del link; acá solo se define la contraseña.
 */
export function SetPasswordPage() {
  // Un link vencido o ya usado llega con error en la URL: se avisa de entrada.
  const [status, setStatus] = useState<'checking' | 'ready' | 'invalid' | 'done'>(authLinkOnLoad?.kind === 'error' ? 'invalid' : 'checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfaFactor, setMfaFactor] = useState<string | null>(null);
  const [code, setCode] = useState('');

  useEffect(() => {
    if (status === 'ready') void mfaFactorForPasswordChange().then(setMfaFactor, () => setMfaFactor(null));
  }, [status]);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) setStatus((s) => (s === 'checking' ? 'ready' : s));
    });
    // Si el link no trae sesión (vencido o ya usado), se avisa después de un momento.
    const timeout = setTimeout(() => setStatus((s) => (s === 'checking' ? 'invalid' : s)), 4_000);
    void supabase.auth.getSession().then(({ data: d }) => d.session && setStatus('ready'));
    return () => {
      data.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const invalid = validatePassword(password) ?? (password !== confirm ? 'Las contraseñas no coinciden.' : null);
    if (invalid) return setError(invalid);
    setBusy(true);
    setError(null);
    try {
      if (mfaFactor) {
        await verifyMfaCode(mfaFactor, code);
        setMfaFactor(null);
      }
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      await supabase.auth.signOut({ scope: 'local' });
      setStatus('done');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-canvas flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img src="/favicon.svg" alt="" className="mx-auto size-14" />
          <h1 className="mt-3 text-2xl font-semibold">{env.appName}</h1>
          <p className="mt-1 text-sm text-slate-500">Definí tu contraseña</p>
        </div>
        <Card className="space-y-4">
          {status === 'checking' && (
            <div className="flex justify-center py-6 text-slate-500">
              <Spinner />
            </div>
          )}
          {status === 'invalid' && (
            <Alert tone="error" title="El link no es válido">
              Puede haber vencido o ya se usó (a veces el correo lo abre solo para revisarlo). Pedí uno nuevo y abrilo apenas llegue.
            </Alert>
          )}
          {status === 'done' && (
            <>
              <Alert tone="success" title="¡Listo!">
                Tu contraseña quedó guardada. Ya podés configurar tu dispositivo con tu email y esa contraseña.
              </Alert>
              <Button block size="lg" onClick={() => location.assign('/')}>
                Continuar
              </Button>
            </>
          )}
          {status === 'ready' && (
            <form className="space-y-4" onSubmit={onSubmit}>
              {error && <Alert tone="error">{error}</Alert>}
              <Field label="Nueva contraseña" hint="Al menos 8 caracteres.">
                {(id) => (
                  <PasswordInput
                    id={id}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Repetí la contraseña">
                {(id) => (
                  <PasswordInput
                    id={id}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                )}
              </Field>
              {mfaFactor && (
                <Field label="Código de verificación" hint="Tu cuenta tiene verificación en dos pasos: escribí los 6 números de tu app de códigos.">
                  {(id) => <Input id={id} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />}
                </Field>
              )}
              <Button type="submit" size="lg" block loading={busy}>
                Guardar contraseña
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
